/**
 * Global navigation guard for unsaved changes.
 *
 * Components with unsaved changes register an async check function
 * via `setPendingChangesGuard()`. The router's global `beforeEach`
 * calls `runPendingChangesGuard()` before allowing navigation.
 * If the guard returns `false`, navigation is aborted.
 *
 * Usage in a component:
 * - `mounted()`: call `setPendingChangesGuard(() => this.confirmDiscard())`
 * - `beforeDestroy()`: call `clearPendingChangesGuard()`
 */

import { isIntentionalNavigation } from "@/utils/IntentionalNavigation";

/** Returns `true` to allow navigation, `false` to block. */
type GuardFn = () => Promise<boolean>;

let activeGuard: GuardFn | null = null;

/** Register a guard function that will be checked before every navigation. */
export function setPendingChangesGuard(guard: GuardFn): void {
  activeGuard = guard;
}

/** Remove the current guard (call in `beforeDestroy`). */
export function clearPendingChangesGuard(): void {
  activeGuard = null;
}

/**
 * Run the registered guard. Returns `true` if navigation may proceed
 * (no guard registered, or the guard allowed it). Returns `false` to block.
 */
export async function runPendingChangesGuard(): Promise<boolean> {
  // The app is already reloading on the user's own request (e.g. applying a new build). Prompting
  // about unsaved changes now would ask them to confirm the button they just pressed.
  if (isIntentionalNavigation()) return true;

  if (!activeGuard) return true;
  return activeGuard();
}
